import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Box, Typography, Button } from "@mui/material";
import { setSessionWarningNotifiedResult } from "../../../store/slices/sessionSlice";
import { AppDispatch, RootState } from "../../../store/store";
import { useDispatch, useSelector } from "react-redux";

interface loginRedirectProps {
  open: boolean;
  remainingTime: number;
  resetSession: () => void;
}

const LoginRedirect: React.FC<loginRedirectProps> = ({
  open,
  remainingTime,
  resetSession,
}) => {
  const navigate = useNavigate();
  const sessionKey = "sessionData";
  const [countdown, setCountdown] = useState<number>(remainingTime);
  const dispatch = useDispatch<AppDispatch>();
  const sessionWarningNotifiedResult = useSelector(
    (state: RootState) => state.session?.sessionWarningNotified
  );

  useEffect(() => {
    if (open) {
      setCountdown(remainingTime);
    }
  }, [open, remainingTime]);

  useEffect(() => {
    if (!open) return;

    const originalTitle = document.title;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          sessionStorage.removeItem(sessionKey);
          document.title = originalTitle;
          navigate("/"); // Redirect to login
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      document.title = originalTitle; // Restore original title when modal closes
    };
  }, [open, navigate]);

  const sendSessionWarningNotification = useCallback(() => {
    if (Notification.permission === "granted") {
      const notification = new Notification("Text Sentinel Session Timeout Warning", {
        body: `Your session will expire in ${(remainingTime / 60).toFixed(
          2
        )} mins. Click to continue.`,
        icon: "brand-logo.svg", // Optional: Add your app's favicon
        silent: true, // Avoid unnecessary sound
      });

      // Auto-dismiss the notification after 5 seconds
      setTimeout(() => notification.close(), 10000);

      // Bring tab into focus if the user clicks the notification
      notification.onclick = () => window.focus();
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          sendSessionWarningNotification();
        }
      });
    }
  }, [remainingTime]);

  useEffect(() => {
    if (open) {
      document.title = `Session Timeout in ${Math.floor(
        countdown / 60
      )}:${String(countdown % 60).padStart(2, "0")}`;

      if (document.hidden && !sessionWarningNotifiedResult) {
        dispatch(setSessionWarningNotifiedResult(true));
        sendSessionWarningNotification();
      }
    }
  }, [
    countdown,
    open,
    sessionWarningNotifiedResult,
    dispatch,
    sendSessionWarningNotification,
  ]);

  const handleClose = () => {
    sessionStorage.removeItem(sessionKey);
    navigate("/"); // Redirect to login page
  };

  const handleContinue = () => {
    resetSession(); // Reset the session and close the modal
    dispatch(setSessionWarningNotifiedResult(false));
  };

  return (
    <Modal
      open={open}
      aria-labelledby="session-modal-title"
      aria-describedby="session-modal-description"
      className="sessionTimeOutModal"
    >
      <Box
        sx={{
          position: "absolute" as const,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: "10px",
          textAlign: "center",
        }}
      >
        <Typography id="session-modal-title" variant="h6" component="h2">
          Session Timeout
        </Typography>
        <Typography
          id="session-modal-description"
          sx={{ mt: 2, mb: 4 }}
          color="textSecondary"
        >
          Your session will expire in{" "}
          <strong>
            {Math.floor(countdown / 60)}:
            {String(countdown % 60).padStart(2, "0")}
          </strong>{" "}
          minutes due to inactivity. Would you like to continue with the current
          session ?
        </Typography>
        <div className="actionBtns">
          <Button
            variant="contained"
            color="primary"
            onClick={handleContinue}
            autoFocus
          >
            Continue
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
            autoFocus
          >
            Redirect to login
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default LoginRedirect;
